import Header from './header/header';
import ScrollLockAnimation from './scroll-lock-animation';

const settings = {
    classNames: {
        hash: 'hash',
    },
};

let initialized = false;

export default class AnchorLinks {
    static init() {
        if (initialized) {
            return;
        }

        initialized = true;

        // Just use the default browser behavior if no scroll
        // lock animations are present on the page - this class
        // was mainly used back when pages had anchor links prior
        // to the 2023 "3.1" updates when every page had a hero
        // that involved scroll locking, but this may be needed
        // again in the future
        if (!ScrollLockAnimation.scrollLockAnimationsPresent) {
            return;
        }

        // Check if a hash is present in the URL
        let hashClassAdded = false;
        if (window.location.hash) {
            // Temporarily add a "hash" class to the body element
            // to hide the page's content while we initialize -
            // this is to prevent a brief flicker if we choose to
            // scroll downward
            document.body
                .classList
                .add(settings.classNames.hash);

            hashClassAdded = true;
        }

        // Check the initial scroll position
        const loadScrollPosition = window.pageYOffset;

        // Find all anchor links on the page
        const anchorLinks = document.querySelectorAll('a[href^="#"');
        for (let i=0; i<anchorLinks.length; i++) {
            anchorLinks[i].addEventListener('click', anchorLinkClickCallback);
        }

        const hashChangeCallback = () => {
            // If a hash was included in the URL, jump to that location on the page
            if (window.location.hash.length <= 1) {
                return false;
            }

            const scrollTarget = ScrollLockAnimation
                .getScrollTargetForSelector(window.location.hash);

            if (!scrollTarget) {
                return false;
            }

            window.scrollTo({
                left: 0,
                top: scrollTarget.position -
                Header.current.container.clientHeight,
                behavior: 'auto',
            });

            // Make sure header is visible
            Header.current.setVisibility(true);

            // This indicates that we found an anchor and scrolled to it
            return true;
        };

        window.addEventListener('hashchange', hashChangeCallback);
        window.addEventListener('load', () => {
            setTimeout(
                () => {
                    const finishCallback = () => {
                        // Remove "hash" class, if it was added
                        if (hashClassAdded) {
                            document.body
                                .classList
                                .remove(settings.classNames.hash);
                        }
                    };

                    const scrolledToAnchor = hashChangeCallback();
                    if (scrolledToAnchor) {
                        // Perform finish callback
                        finishCallback();

                        // Stop here
                        return;
                    }

                    // We haven't scrolled to an anchor - if the browser
                    // loaded at a particular scroll location, scroll to
                    // it in case we've started scroll jacking
                    if (loadScrollPosition > 0) {
                        window.scrollTo({
                            left: 0,
                            top: loadScrollPosition,
                            behavior: 'auto',
                        });
                    }

                    // Perform finish callback
                    finishCallback();
                },
                1,
            );
        });
    }
}

/*
    Internal functions
*/

function anchorLinkClickCallback(event) {
    event.preventDefault();
    event.stopPropagation();

    // Note this would break if there was a query string after the hash
    const match = /#(.*)$/.exec(event.target.href);
    if (!match) {
        return;
    }

    if (match[1].length == 0) {
        // We're navigating to the top of the page - do nothing for now
        return;
    }

    // If the element is on the page, translate its visual position in the
    // content to the scroll location that incorporates any distance added
    // due to animations
    const scrollTarget = ScrollLockAnimation
        .getScrollTargetForSelector(match[0]);

    if (!scrollTarget) {
        return;
    }

    // If a jump value was provided, first scroll to that position immediately -
    // this allows us to avoid scrolling through hero animations
    if (scrollTarget.jump > 0) {
        if (scrollTarget.jump > window.pageYOffset) {
            window.scrollTo({
                left: 0,
                top: scrollTarget.jump,
                behavior: 'auto',
            });
        }
    }

    window.scrollTo({
        left: 0,
        top: scrollTarget.position,
        behavior: 'smooth',
    });

    // Hide the header if open
    Header.current.setVisibility(false);
}
